// Copyright (c) StrangeBee 2024

import { ICONS } from '@Constants/iconsApp';
import SortableItemList from '@Atoms/SortableItemList/SortableItemList';
import { classList, useLocalMessage } from '@Helpers/';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THForm } from '@Molecules/THForm/THForm';
import Form, { FormInstance } from 'antd/lib/form';
import React, { ReactElement, ReactNode } from 'react';
import { FormListFieldData } from 'antd/lib/form/FormList';

import './CreateSortableListItem.scss';

interface CreateSortableListItemProps<T> {
    field: FormListFieldData;
    index: number;
    form: FormInstance;
    fieldName: string;
    editDrawer?: (onFinish: (item: T) => void, triggerElem: ReactElement) => ReactElement;
    title: ReactNode;
    remove: (name: number) => void;
    onMove?: (item?: T, newIndex?: number, oldIndex?: number) => void;
    className?: string;
    removeIcon?: ReactNode;
}

export const CreateSortableListItem = <T,>({
    field,
    index,
    form,
    remove,
    fieldName,
    editDrawer,
    title,
    onMove,
    className,
    removeIcon,
}: CreateSortableListItemProps<T>): ReactElement => {
    const i = useLocalMessage();

    const { name, key } = field;

    const items = Form.useWatch<T[]>(fieldName, form);

    const editItem = (newItem: any): void => {
        form.setFieldsValue({
            [fieldName]: items.map((item, index) => (index === name ? newItem : item)),
        });
    };

    const moveItem = (newIndex: number, oldIndex: number) => {
        const element = items[oldIndex];
        items.splice(oldIndex, 1);
        items.splice(newIndex, 0, element);

        onMove?.(element, newIndex, oldIndex);

        form.setFieldsValue({
            [fieldName]: items,
        });
    };

    return (
        <SortableItemList dragType="case-template-task" index={index} moveItem={moveItem}>
            <THForm.Item key={key} noStyle>
                <div className={classList('sortable-list-item', className)}>
                    <div className="sortable-list-item__title">
                        <ICONS.MenuOutlined />
                        {title}
                    </div>

                    <div className="sortable-list-item__actions">
                        {editDrawer?.(editItem, <THButton type="link">{i('general.edit')}</THButton>)}

                        <THButton type="link" onClick={() => remove(name)} danger={!removeIcon}>
                            {removeIcon || i('general.delete')}
                        </THButton>
                    </div>
                </div>
            </THForm.Item>
        </SortableItemList>
    );
};
